import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { type CSSProperties, useState } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { css } from '@sorare/css';

import type {
  CardPullRulesDialog_cardPullConfig,
  CardPullRulesDialog_cardShardsChest,
} from '#src/components/essence/CardPullExperience/CardPullRulesDialog/__generated__/index.graphql.ts';
import {
  CardPullRulesDialog,
  TabValue,
} from '#src/components/essence/CardPullExperience/CardPullRulesDialog/index.tsx';

import type {
  CompetitionAvailability_cardShardsChest,
  PlayerOriginLeaguesQuery,
  PlayerOriginLeaguesQueryVariables,
} from './__generated__/index.graphql.ts';

const scroller = css`
  width: 100%;
  max-width: var(--100vw);
  margin-top: var(--unit);
`;

const strip = css`
  display: flex;
  align-items: center;
  gap: var(--triple-unit);
  flex: none;
  margin: 0 auto;
  &::before,
  &::after {
    content: '';
    flex: none;
    width: 0;
  }
`;

const segment = css`
  display: flex;
  align-items: center;
  gap: var(--double-unit);
  flex: none;
  padding: var(--unit);
  border-radius: var(--triple-unit);
  background: var(--c-nd-50);
  backdrop-filter: blur(32px);
`;

const competition = css`
  position: relative;
  flex: none;
  line-height: 0;
`;

const AVATAR_SIZE = 24;

const statusDot = css`
  position: absolute;
  right: 0;
  bottom: 0;
  width: var(--unit);
  height: var(--unit);
  border-radius: var(--unit);
  background: var(--status-color);
  box-shadow: 0 0 0 calc(var(--half-unit) / 2) var(--c-black);
`;

const PLAYER_ORIGIN_LEAGUES_QUERY = gql`
  query PlayerOriginLeaguesQuery {
    so5 {
      id
      so5Competitions(sport: FOOTBALL) {
        slug
        displayName
        iconUrl
        competitions {
          slug
        }
      }
    }
  }
` as TypedDocumentNode<
  PlayerOriginLeaguesQuery,
  PlayerOriginLeaguesQueryVariables
>;

type Props = {
  // Don't spread CardPullRulesDialog into this component's own fragment: CardPullInfos already
  // pulls it, and a second spread double-counts the odds tables past the query complexity limit.
  cardShardsChest: CompetitionAvailability_cardShardsChest &
    CardPullRulesDialog_cardShardsChest;
  cardPullConfigs: CardPullRulesDialog_cardPullConfig[];
};

export const CompetitionAvailability = ({
  cardShardsChest,
  cardPullConfigs,
}: Props) => {
  const [open, setOpen] = useState(false);
  const { sport } = useSportContext();
  const isFootball = sport === Sport.FOOTBALL;
  const { data } = useQuery(PLAYER_ORIGIN_LEAGUES_QUERY, {
    skip: !isFootball,
  });

  if (!isFootball) {
    return null;
  }

  const availableSlugs = new Set(
    cardShardsChest.availableCompetitions.map(({ slug }) => slug)
  );

  const leagues = (data?.so5.so5Competitions || [])
    .filter(({ competitions }) => competitions.length === 1)
    .map(({ slug, displayName, iconUrl, competitions }) => ({
      slug,
      name: displayName,
      avatarUrl: iconUrl,
      available: availableSlugs.has(competitions[0].slug),
    }));

  const availableLeagues = leagues.filter(({ available }) => available);
  const unavailableLeagues = leagues.filter(({ available }) => !available);

  if (leagues.length === 0) {
    return null;
  }

  const renderLeague = (
    league: { slug: string; name: string; avatarUrl: string },
    available: boolean
  ) => (
    <span key={league.slug} className={competition}>
      <LeagueAvatar
        league={league}
        size={AVATAR_SIZE}
        className={classNames({ 'opacity-50': !available })}
      />
      <span
        className={statusDot}
        style={
          {
            '--status-color': available
              ? 'var(--c-score-high)'
              : 'var(--c-red-400)',
          } as CSSProperties
        }
      />
    </span>
  );

  return (
    <>
      <CardPullRulesDialog
        open={open}
        onClose={() => setOpen(false)}
        initialTab={TabValue.AVAILABLE_COMPETITIONS}
        cardShardsChest={cardShardsChest}
        cardPullConfigs={cardPullConfigs}
      />
      <FullViewportScroll className={scroller}>
        <ButtonBase className={strip} onClick={() => setOpen(true)}>
          {availableLeagues.length > 0 && (
            <span className={segment}>
              {availableLeagues.map(league => renderLeague(league, true))}
            </span>
          )}
          {unavailableLeagues.length > 0 && (
            <span className={segment}>
              {unavailableLeagues.map(league => renderLeague(league, false))}
            </span>
          )}
        </ButtonBase>
      </FullViewportScroll>
    </>
  );
};

CompetitionAvailability.fragments = {
  cardShardsChest: gql`
    fragment CompetitionAvailability_cardShardsChest on CardShardsChest {
      id
      availableCompetitions {
        slug
        name
      }
    }
  ` as TypedDocumentNode<CompetitionAvailability_cardShardsChest>,
};
